// Error Detection System for VRP Unified Architecture
import { VRPError, VRPErrorCategory, VRPErrorSeverity } from '../types/errors';

export class VRPErrorDetector {
  private static readonly REACT_ERROR_185_PATTERNS = [
    /Cannot read prop.*of undefined/,
    /Cannot read properties of undefined/,
    /useProject must be used within a ProjectProvider/,
    /useStableProject must be used within a StableProjectProvider/,
    /Cannot access.*before initialization/,
    /Context.*is undefined/
  ];

  private static readonly INFINITE_LOOP_PATTERNS = [
    /Maximum update depth exceeded/,
    /Too many re-renders/,
    /React has detected a change.*during render/
  ];

  private static readonly NAVIGATION_ERROR_PATTERNS = [
    /Cannot read property.*of null.*router/,
    /useNavigate.*must be used within.*Router/,
    /useParams.*must be used within.*Router/,
    /Location.*is not defined/
  ];

  private static readonly NETWORK_ERROR_PATTERNS = [
    /NetworkError/,
    /fetch.*failed/,
    /ERR_NETWORK/,
    /ERR_INTERNET_DISCONNECTED/,
    /CORS.*blocked/
  ];

  static categorizeError(error: Error): VRPErrorCategory {
    const message = error.message;

    // Check for React Error #185 patterns
    if (this.REACT_ERROR_185_PATTERNS.some(pattern => pattern.test(message))) {
      return VRPErrorCategory.CONTEXT_ERROR;
    }

    // Check for infinite loop patterns
    if (this.INFINITE_LOOP_PATTERNS.some(pattern => pattern.test(message))) {
      return VRPErrorCategory.STATE_MANAGEMENT_ERROR;
    }

    // Check for navigation errors
    if (this.NAVIGATION_ERROR_PATTERNS.some(pattern => pattern.test(message))) {
      return VRPErrorCategory.NAVIGATION_ERROR;
    }

    // Check for network errors
    if (this.NETWORK_ERROR_PATTERNS.some(pattern => pattern.test(message))) {
      return VRPErrorCategory.NETWORK_ERROR;
    }

    // Check error type and name
    if (error.name === 'TypeError') {
      return VRPErrorCategory.COMPONENT_ERROR;
    }

    if (error.name === 'ReferenceError') {
      return VRPErrorCategory.COMPONENT_ERROR;
    }

    if (error.name === 'SyntaxError') {
      return VRPErrorCategory.COMPONENT_ERROR;
    }

    // Default to unknown error
    return VRPErrorCategory.UNKNOWN_ERROR;
  }

  static determineSeverity(error: Error, category: VRPErrorCategory): VRPErrorSeverity {
    // Context errors are critical as they break the app
    if (category === VRPErrorCategory.CONTEXT_ERROR) {
      return VRPErrorSeverity.CRITICAL;
    }

    // State management errors are critical as they cause infinite loops
    if (category === VRPErrorCategory.STATE_MANAGEMENT_ERROR) {
      return VRPErrorSeverity.CRITICAL;
    }

    // Navigation errors prevent user from using the app
    if (category === VRPErrorCategory.NAVIGATION_ERROR) {
      return VRPErrorSeverity.HIGH;
    }

    // Network errors affect functionality but app remains usable
    if (category === VRPErrorCategory.NETWORK_ERROR) {
      return VRPErrorSeverity.MEDIUM;
    }

    // Component errors affect specific features
    if (category === VRPErrorCategory.COMPONENT_ERROR) {
      return VRPErrorSeverity.MEDIUM;
    }

    // Permission errors prevent access but don't break the app
    if (category === VRPErrorCategory.PERMISSION_ERROR) {
      return VRPErrorSeverity.MEDIUM;
    }

    // Default to medium severity
    return VRPErrorSeverity.MEDIUM;
  }

  static createVRPError(
    originalError: Error,
    context: Partial<VRPError['context']> = {}
  ): VRPError {
    const category = this.categorizeError(originalError);
    const severity = this.determineSeverity(originalError, category);

    return {
      id: this.generateErrorId(),
      category,
      severity,
      message: originalError.message,
      originalError,
      context: {
        route: window.location.pathname,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId(),
        stack: originalError.stack,
        ...context
      },
      recovered: false,
      retryCount: 0
    };
  }

  static isReactError185(error: Error): boolean {
    return this.REACT_ERROR_185_PATTERNS.some(pattern => 
      pattern.test(error.message)
    );
  }

  static isInfiniteLoop(error: Error): boolean {
    return this.INFINITE_LOOP_PATTERNS.some(pattern => 
      pattern.test(error.message)
    );
  }

  static isNavigationError(error: Error): boolean {
    return this.NAVIGATION_ERROR_PATTERNS.some(pattern => 
      pattern.test(error.message)
    );
  }

  static isNetworkError(error: Error): boolean {
    return this.NETWORK_ERROR_PATTERNS.some(pattern => 
      pattern.test(error.message)
    );
  }

  private static generateErrorId(): string {
    return `vrp_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private static getSessionId(): string {
    let sessionId = sessionStorage.getItem('vrp_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('vrp_session_id', sessionId);
    }
    return sessionId;
  }
}